/**
 * The Quiz stage (plan docs/plans/lms-plan.md 7.2, 7.3, R9, R21, 18.11, 18.15;
 * Appendix A "Progress, attempts, completions"; WP-40).
 *
 * Screens: the intro (what the quiz is, Start), one question per screen with
 * a progress count, then the feedback, then the results.
 *
 * - Start posts an attempt. The server draws the questions and the option
 *   order and stores the draw; the player shows them exactly in the order it
 *   answers (R21). A second start RESUMES the in-progress attempt (200, 18.15).
 * - The gates are the server's (409 LMS_SLIDES_INCOMPLETE, LMS_READING_REQUIRED,
 *   LMS_ALREADY_CURRENT unless forced, LMS_ATTEMPT_LIMIT with retry_at); the
 *   player also keeps Start disabled while its own view of the gate is shut.
 * - Feedback (18.15): the drawn questions carry no correct flag, and the
 *   submit answer carries the full per-option feedback for BOTH modes, with
 *   feedback_mode echoed. after_each is presented by revealing it question by
 *   question straight after the submit; at_end shows it all on the results.
 * - Results: the score against the pass mark. A pass opens the attestation
 *   (7.3) and posts complete; a fail shows the sections to review (the
 *   sections of the missed questions) and Retake, subject to the limits.
 *
 * A pass that was never attested (the tab closed on the results) is not
 * lost: the passed attempt's id is remembered in this browser until the
 * completion is written, and the intro offers to finish it (GET
 * /attempts/:id answers the results once submitted, 18.15; complete is
 * idempotent). Only the attempt id is kept, never an answer or a score.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { format } from 'date-fns';
import { AlertTriangle, ChevronLeft, ChevronRight, Lock, RotateCcw } from 'lucide-react';
import { apiErrorCode, apiErrorMessage } from '../../api/client';
import { getAttempt, startAttempt, submitAttempt } from '../../api/attempts';
import type { AttemptQuestion, AttemptQuizInfo, AttemptSummary, SubmitResult } from '../../api/attempts';
import { completeAttempt } from '../../api/completions';
import type { CompletionResult } from '../../api/completions';
import type { CourseSection, FeedbackMode, QuizSettings } from '../../content/types';
import { outlineBtnCls, primaryBtnCls, secondaryBtnCls } from '../../../ui/styles';
import { useToast } from '../../../ui/Toast';
import { AttestDialog } from './AttestDialog';
import { QuestionFeedback } from './QuestionFeedback';
import { headingCls, microLabelCls, noticeCls, optionCls, panelCls, pillCls, stageCls } from './styles';

export interface QuizStageProps {
  courseId: string;
  userId: string | null;
  assignmentId: string | null;
  /** The player's view of the gate (7.2), or null when open. */
  lock: string | null;
  quizSettings: Partial<QuizSettings>;
  sections: CourseSection[];
  signerName: string | null;
  signerEmail: string | null;
  onGoToSlides: () => void;
  onGoToReading: () => void;
  onReview: (sectionId: string, where: 'reading' | 'slides') => void;
  onCompleted: (result: CompletionResult) => void;
}

type Phase = 'intro' | 'answering' | 'reveal' | 'results';

interface StartError {
  code: string | null;
  message: string;
  retryAt: string | null;
}

/** When a retry time falls, in words (the learner's own clock). */
export function formatWhen(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return format(d, "d MMM yyyy 'at' h:mm a");
}

function startErrorOf(err: unknown): StartError {
  const code = apiErrorCode(err);
  const data = (err as { response?: { data?: { retry_at?: unknown } } } | null)?.response?.data;
  const retryAt = typeof data?.retry_at === 'string' ? data.retry_at : null;
  switch (code) {
    case 'LMS_SLIDES_INCOMPLETE':
      return { code, retryAt, message: 'Finish every slide before the quiz.' };
    case 'LMS_READING_REQUIRED':
      return { code, retryAt, message: 'Read the full document and mark it as read before the quiz.' };
    case 'LMS_ALREADY_CURRENT':
      return {
        code,
        retryAt,
        message: 'You already hold a current completion of this course. You can review the slides and reading, or take the quiz again anyway.',
      };
    case 'LMS_ATTEMPT_LIMIT':
      return {
        code,
        retryAt,
        message: retryAt
          ? `You have used the quiz attempts allowed for now. You can try again on ${formatWhen(retryAt)}.`
          : 'You have used the quiz attempts allowed for now. Try again later.',
      };
    case 'LMS_NOT_PUBLISHED':
      return { code, retryAt, message: 'This course has no published quiz yet.' };
    default:
      return { code, retryAt, message: apiErrorMessage(err, 'The quiz could not be started.') };
  }
}

function storageKey(userId: string | null, courseId: string): string | null {
  return userId ? `lms.player.passedAttempt.${userId}.${courseId}` : null;
}

function readStored(key: string | null): string | null {
  if (!key) return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key: string | null, value: string | null): void {
  if (!key) return;
  try {
    if (value) window.localStorage.setItem(key, value);
    else window.localStorage.removeItem(key);
  } catch {
    // Storage refused (a private window): the pass can still be attested now.
  }
}

const FEEDBACK_WORDS: Record<FeedbackMode, string> = {
  after_each: 'feedback on each question as soon as you submit',
  at_end: 'feedback on every question with your results',
};

export function QuizStage(props: QuizStageProps) {
  const {
    courseId,
    userId,
    assignmentId,
    lock,
    quizSettings,
    sections,
    signerName,
    signerEmail,
    onGoToSlides,
    onGoToReading,
    onReview,
    onCompleted,
  } = props;
  const { toast } = useToast();
  const key = storageKey(userId, courseId);

  const [phase, setPhase] = useState<Phase>('intro');
  const [attempt, setAttempt] = useState<AttemptSummary | null>(null);
  const [questions, setQuestions] = useState<AttemptQuestion[]>([]);
  const [quizInfo, setQuizInfo] = useState<AttemptQuizInfo | null>(null);
  const [answers, setAnswers] = useState<Record<string, string[]>>({});
  const [qIndex, setQIndex] = useState(0);
  const [result, setResult] = useState<SubmitResult | null>(null);
  const [revealIndex, setRevealIndex] = useState(0);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<StartError | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [recovered, setRecovered] = useState(false);
  const [attestOpen, setAttestOpen] = useState(false);
  const [attestBusy, setAttestBusy] = useState(false);
  const [attestError, setAttestError] = useState<string | null>(null);
  const [, setClockTick] = useState(0);
  const busyRef = useRef(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // A pass never attested in an earlier visit (see the file header).
  useEffect(() => {
    const stored = readStored(key);
    if (!stored) return undefined;
    let cancelled = false;
    getAttempt(stored)
      .then((detail) => {
        if (cancelled || !mounted.current) return;
        const a = detail.attempt;
        if (a && a.status === 'passed' && Array.isArray(detail.results)) {
          setAttempt(a);
          setQuestions(Array.isArray(detail.questions) ? detail.questions : []);
          setQuizInfo(detail.quiz ?? null);
          setResult({
            attempt: { id: a.id, status: a.status, score_pct: a.score_pct ?? 0, passed: true, submitted_at: a.submitted_at ?? '' },
            results: detail.results,
            review_sections: detail.review_sections ?? [],
            can_retry_at: null,
            feedback_mode: detail.quiz?.feedback_mode,
          });
          setRecovered(true);
          setPhase('results');
        } else {
          writeStored(key, null);
        }
      })
      .catch(() => {
        if (!cancelled) writeStored(key, null);
      });
    return () => {
      cancelled = true;
    };
  }, [key]);

  const passMark = quizInfo?.pass_mark_pct ?? quizSettings.pass_mark_pct ?? null;
  const feedbackMode: FeedbackMode =
    result?.feedback_mode ?? quizInfo?.feedback_mode ?? quizSettings.feedback_mode ?? 'after_each';

  const begin = useCallback(
    async (force = false) => {
      // 7.2: the quiz waits for its gate; ?force=1 only lifts LMS_ALREADY_CURRENT, never the gate.
      if (busyRef.current || lock) return;
      busyRef.current = true;
      setStarting(true);
      setStartError(null);
      try {
        const started = await startAttempt(courseId, { assignmentId, force });
        if (!mounted.current) return;
        setAttempt(started.attempt);
        setQuestions(Array.isArray(started.questions) ? started.questions : []);
        setQuizInfo(started.quiz ?? null);
        setAnswers({});
        setQIndex(0);
        setResult(null);
        setRevealIndex(0);
        setSubmitError(null);
        setRecovered(false);
        setPhase('answering');
        if (started.resumed) toast('Your quiz in progress was resumed with the same questions.', { type: 'info' });
      } catch (err) {
        if (mounted.current) {
          setStartError(startErrorOf(err));
          setPhase('intro');
        }
      } finally {
        busyRef.current = false;
        if (mounted.current) setStarting(false);
      }
    },
    [courseId, assignmentId, lock, toast]
  );

  const current = questions[qIndex];
  const currentAnswer = current ? answers[current.id] ?? [] : [];
  const allAnswered = questions.length > 0 && questions.every((q) => (answers[q.id] ?? []).length > 0);

  const choose = (question: AttemptQuestion, optionId: string) => {
    setAnswers((prev) => {
      const cur = prev[question.id] ?? [];
      const nextSel =
        question.kind === 'multi'
          ? cur.includes(optionId)
            ? cur.filter((id) => id !== optionId)
            : [...cur, optionId]
          : [optionId];
      return { ...prev, [question.id]: nextSel };
    });
  };

  const submit = async (e?: FormEvent) => {
    if (e) e.preventDefault();
    if (!attempt || busyRef.current || !allAnswered) return;
    busyRef.current = true;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const out = await submitAttempt(attempt.id, answers);
      if (!mounted.current) return;
      setResult(out);
      setRevealIndex(0);
      writeStored(key, out.attempt.passed ? attempt.id : null);
      const mode = out.feedback_mode ?? quizInfo?.feedback_mode ?? quizSettings.feedback_mode ?? 'after_each';
      setPhase(mode === 'after_each' ? 'reveal' : 'results');
    } catch (err) {
      if (!mounted.current) return;
      if (apiErrorCode(err) === 'LMS_ATTEMPT_EXPIRED') {
        setStartError({ code: 'LMS_ATTEMPT_EXPIRED', retryAt: null, message: 'This attempt expired before it was submitted. Start a new one.' });
        setPhase('intro');
      } else {
        setSubmitError(apiErrorMessage(err, 'Your answers could not be submitted. Try again.'));
      }
    } finally {
      busyRef.current = false;
      if (mounted.current) setSubmitting(false);
    }
  };

  const attest = async () => {
    if (!attempt || attestBusy) return;
    setAttestBusy(true);
    setAttestError(null);
    try {
      const out = await completeAttempt(attempt.id);
      if (!mounted.current) return;
      writeStored(key, null);
      setAttestOpen(false);
      onCompleted(out);
    } catch (err) {
      if (!mounted.current) return;
      setAttestError(
        apiErrorCode(err) === 'LMS_VERSION_CHANGED'
          ? 'This course has a new version since you took the quiz. Take its quiz again.'
          : apiErrorMessage(err, 'Your completion could not be recorded. Try again.')
      );
    } finally {
      if (mounted.current) setAttestBusy(false);
    }
  };

  // Re-render when a retake becomes allowed (the results' can_retry_at).
  const retryAt = result && !result.attempt.passed ? result.can_retry_at : null;
  const retryMs = retryAt ? Date.parse(retryAt) : NaN;
  const retryWaiting = Number.isFinite(retryMs) && retryMs > Date.now();
  useEffect(() => {
    if (!retryWaiting) return undefined;
    const delay = Math.min(Math.max(retryMs - Date.now(), 0) + 500, 2147483000);
    const timer = window.setTimeout(() => setClockTick((t) => t + 1), delay);
    return () => window.clearTimeout(timer);
  }, [retryWaiting, retryMs]);

  // ---------------------------------------------------------------------------
  // Screens
  // ---------------------------------------------------------------------------

  if (phase === 'answering' && current) {
    const multi = current.kind === 'multi';
    const isLast = qIndex === questions.length - 1;
    return (
      <div className={stageCls} data-testid="lms-quiz-question">
        <form className={`${panelCls} space-y-4`} onSubmit={submit}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className={microLabelCls} data-testid="lms-quiz-count">{`Question ${qIndex + 1} of ${questions.length}`}</span>
            <div
              className="h-1.5 w-32 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700"
              role="progressbar"
              aria-label="Quiz progress"
              aria-valuemin={1}
              aria-valuemax={questions.length}
              aria-valuenow={qIndex + 1}
            >
              <div className="h-full bg-primary-600 dark:bg-primary-500" style={{ width: `${Math.round(((qIndex + 1) / questions.length) * 100)}%` }} />
            </div>
          </div>
          <p className={headingCls} id={`lms-quiz-stem-${current.id}`} data-testid="lms-quiz-stem" data-question-id={current.id}>
            {current.stem}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">{multi ? 'Choose every answer that applies.' : 'Choose one answer.'}</p>
          <fieldset aria-labelledby={`lms-quiz-stem-${current.id}`} className="space-y-2">
            {current.options.map((option) => {
              const isSelected = currentAnswer.includes(option.id);
              return (
                <label key={option.id} className={optionCls(isSelected ? 'selected' : 'idle')} data-testid="lms-quiz-option" data-option-id={option.id}>
                  <input
                    type={multi ? 'checkbox' : 'radio'}
                    name={`lms-quiz-${current.id}`}
                    value={option.id}
                    checked={isSelected}
                    onChange={() => choose(current, option.id)}
                    className="mt-0.5 h-4 w-4 flex-shrink-0 border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                  />
                  <span className="min-w-0 flex-1">{option.text}</span>
                </label>
              );
            })}
          </fieldset>
          {submitError && (
            <div className={noticeCls('red')} role="alert" data-testid="lms-quiz-submit-error">
              {submitError}
            </div>
          )}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <button
              type="button"
              className={secondaryBtnCls}
              onClick={() => setQIndex((i) => Math.max(0, i - 1))}
              disabled={qIndex === 0 || submitting}
              data-testid="lms-quiz-prev"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
              Previous
            </button>
            {isLast ? (
              <button type="submit" className={primaryBtnCls} disabled={!allAnswered || submitting} data-testid="lms-quiz-submit">
                {submitting ? 'Submitting...' : 'Submit answers'}
              </button>
            ) : (
              <button
                type="button"
                className={primaryBtnCls}
                onClick={() => setQIndex((i) => Math.min(questions.length - 1, i + 1))}
                disabled={currentAnswer.length === 0}
                data-testid="lms-quiz-next"
              >
                Next
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
          </div>
          {isLast && !allAnswered && (
            <p className="text-xs text-gray-500 dark:text-gray-400">Answer every question before you submit.</p>
          )}
        </form>
      </div>
    );
  }

  if (phase === 'reveal' && result) {
    const q = questions[revealIndex];
    const isLast = revealIndex >= questions.length - 1;
    return (
      <div className={stageCls} data-testid="lms-quiz-reveal">
        <div className={`${panelCls} space-y-4`}>
          <div aria-live="polite" data-testid="lms-quiz-feedback-live">
            {q ? (
              <QuestionFeedback
                question={q}
                result={result.results.find((r) => r.question_id === q.id)}
                number={revealIndex + 1}
                total={questions.length}
              />
            ) : null}
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              className={primaryBtnCls}
              onClick={() => (isLast ? setPhase('results') : setRevealIndex((i) => i + 1))}
              data-testid="lms-reveal-next"
            >
              {isLast ? 'See your results' : 'Next question'}
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'results' && result) {
    const passed = result.attempt.passed === true;
    const titleOf = (id: string) => sections.find((s) => s.id === id)?.title ?? id;
    return (
      <div className={`${stageCls} space-y-4`} data-testid="lms-quiz-results">
        <div className={`${panelCls} space-y-3`}>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className={headingCls}>{passed ? 'You passed' : 'Not passed yet'}</h2>
            <span className={pillCls(passed ? 'green' : 'red')} data-testid="lms-quiz-outcome">
              {passed ? 'Passed' : 'Not passed'}
            </span>
          </div>
          <p className="text-sm text-gray-700 dark:text-gray-300" data-testid="lms-quiz-score">
            {`You scored ${result.attempt.score_pct}%.`}
            {passMark !== null ? ` The pass mark is ${passMark}%.` : ''}
          </p>
          {recovered && passed && (
            <div className={noticeCls('primary')} data-testid="lms-quiz-recovered">
              You passed this quiz earlier and have not attested yet. Attest to finish the course.
            </div>
          )}
          {passed ? (
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                className={primaryBtnCls}
                onClick={() => {
                  setAttestError(null);
                  setAttestOpen(true);
                }}
                data-testid="lms-quiz-attest"
              >
                Attest and finish
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {result.review_sections.length > 0 && (
                <div data-testid="lms-review-sections">
                  <div className={microLabelCls}>Sections to review</div>
                  <ul className="mt-2 space-y-1.5">
                    {result.review_sections.map((id) => (
                      <li key={id} className="flex flex-wrap items-center gap-2" data-testid="lms-review-section" data-section-id={id}>
                        <span className="min-w-0 flex-1 text-sm font-medium text-gray-900 dark:text-gray-100">{titleOf(id)}</span>
                        <button type="button" className={outlineBtnCls()} onClick={() => onReview(id, 'reading')}>
                          Reading
                        </button>
                        <button type="button" className={outlineBtnCls()} onClick={() => onReview(id, 'slides')}>
                          Slides
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  className={primaryBtnCls}
                  onClick={() => void begin(false)}
                  disabled={retryWaiting || starting || Boolean(lock)}
                  data-testid="lms-quiz-retake"
                >
                  <RotateCcw className="h-4 w-4" aria-hidden="true" />
                  Retake
                </button>
                {retryWaiting && (
                  <span className="text-xs text-gray-500 dark:text-gray-400" data-testid="lms-retake-wait">
                    {`You can retake on ${formatWhen(retryAt)}.`}
                  </span>
                )}
              </div>
              {startError && (
                <div className={noticeCls('amber')} role="alert" data-testid="lms-quiz-start-error">
                  {startError.message}
                </div>
              )}
            </div>
          )}
        </div>

        {feedbackMode !== 'at_end' && questions.length > 0 && (
          // after_each: the feedback was revealed question by question; it stays one click away.
          <details className={`${panelCls} group`} data-testid="lms-results-review">
            <summary className="flex min-h-[44px] cursor-pointer items-center text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-200 sm:min-h-0">
              Review your answers
            </summary>
            <div className="mt-4 space-y-6">
              {questions.map((q, i) => (
                <QuestionFeedback
                  key={q.id}
                  question={q}
                  result={result.results.find((r) => r.question_id === q.id)}
                  number={i + 1}
                  total={questions.length}
                />
              ))}
            </div>
          </details>
        )}

        {feedbackMode === 'at_end' && (
          <div className={`${panelCls} space-y-6`} data-testid="lms-results-feedback">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Your answers</h3>
            {questions.map((q, i) => (
              <QuestionFeedback
                key={q.id}
                question={q}
                result={result.results.find((r) => r.question_id === q.id)}
                number={i + 1}
                total={questions.length}
              />
            ))}
          </div>
        )}

        <AttestDialog
          open={attestOpen}
          signerName={signerName}
          signerEmail={signerEmail}
          busy={attestBusy}
          error={attestError}
          onAttest={() => void attest()}
          onClose={() => setAttestOpen(false)}
        />
      </div>
    );
  }

  // Intro.
  const drawCount = quizSettings.draw_count;
  const maxPerDay = quizSettings.max_attempts_per_day;
  const cooldown = quizSettings.cooldown_minutes;
  return (
    <div className={stageCls} data-testid="lms-quiz-intro">
      <div className={`${panelCls} space-y-4`}>
        <h2 className={headingCls}>Quiz</h2>
        <ul className="list-disc space-y-1 pl-5 text-sm text-gray-700 dark:text-gray-300">
          {typeof drawCount === 'number' && <li>{`${drawCount} questions, one at a time.`}</li>}
          {passMark !== null && <li>{`The pass mark is ${passMark}%.`}</li>}
          <li>{`You get ${FEEDBACK_WORDS[feedbackMode] ?? FEEDBACK_WORDS.after_each}.`}</li>
          {typeof maxPerDay === 'number' ? (
            <li>{`Up to ${maxPerDay} attempts in a day${typeof cooldown === 'number' && cooldown > 0 ? `, at least ${cooldown} minutes apart` : ''}.`}</li>
          ) : maxPerDay === null ? (
            <li>You may retake it as often as you like.</li>
          ) : null}
        </ul>
        {lock && (
          <div className={noticeCls('amber')} data-testid="lms-quiz-lock">
            <Lock className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
            <div className="min-w-0 space-y-2">
              <div>{lock}</div>
              <div className="flex flex-wrap gap-2">
                <button type="button" className={outlineBtnCls()} onClick={onGoToSlides}>
                  Go to the slides
                </button>
                <button type="button" className={outlineBtnCls()} onClick={onGoToReading}>
                  Go to the reading
                </button>
              </div>
            </div>
          </div>
        )}
        {startError && (
          <div className={noticeCls('amber')} role="alert" data-testid="lms-quiz-start-error">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
            <div className="min-w-0 space-y-2">
              <div>{startError.message}</div>
              {startError.code === 'LMS_ALREADY_CURRENT' && (
                <button type="button" className={outlineBtnCls()} onClick={() => void begin(true)} data-testid="lms-quiz-force">
                  Take the quiz anyway
                </button>
              )}
            </div>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className={primaryBtnCls}
            onClick={() => void begin(false)}
            disabled={Boolean(lock) || starting}
            data-testid="lms-quiz-start"
          >
            {starting ? 'Starting...' : 'Start the quiz'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default QuizStage;
